import { useStoreActions } from 'easy-peasy';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

// import icons
import DeleteIcon from '../../../assets/images/svg/delete-icon.svg';

// tools
import { extractNumericQuantity } from '../../tools/manager';

// modèle pour une ligne matériel
const refMaterial = {
  idMaterial: '',
  name: '',
  reference: '',
  quantity: '',       // numérique pour le back
  quantityLabel: '',  // ce que tape l'utilisateur : "3 u", "250 ml", etc.
};

// data fixes
const createName = 'Vétilla Zobda';
const supplierName = 'Sacarabany SARL';

const PrefillPurchaseOrder = () => {
  // state (données)
  const [customer, setCustomer] = useState();
  const [listeMaterialCreate, setListeMaterialCreate] = useState([{ ...refMaterial }]);
  const [materials, setMaterials] = useState([]);
  const [referencesMaterials, setReferencesMaterials] = useState([]);
  const [numberCoupon, setNumberCoupon] = useState('');
  const [formError, setFormError] = useState(false);
  const [idSaca, setIdSaca] = useState('');

  // params
  const { idCustomer, idWorksite } = useParams();
  const navigate = useNavigate();

  //  🌱 init store actions
  const getNextCounterValue = useStoreActions(
    actions => actions.middleware.CouponPreInterMiddleware.getNextCounterValue
  );
  const getListMaterialSettings = useStoreActions(
    actions => actions.middleware.OrdersMiddleware.getListMaterialSettings
  );
  const createOrderCoupon = useStoreActions(
    actions => actions.middleware.OrdersMiddleware.createOrderCoupon
  );
  const getSacaCompany = useStoreActions(
    actions => actions.middleware.CompanyMiddleware.getSacaCompany
  );
  const getCompany = useStoreActions(
    actions => actions.middleware.OrdersMiddleware.getCompany
  );

  // reset erreur dès que le form change
  useEffect(() => {
    setFormError(false);
  }, [numberCoupon, listeMaterialCreate]);

  // initial load
  useEffect(() => {
    callGetCompany();
    callListMaterialSettings();
    callListSaca();
    callListAllNumberOrderCoupon();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔹 Récup numéro du prochain bon
  const callListAllNumberOrderCoupon = async () => {
    try {
      // 1. Récupérer le prochain numéro côté backend
      const res = await getNextCounterValue("couponPreInter"); // ex: "124"
      setNumberCoupon(res);
    } catch (error) {
      console.log('Error: ', error);
      toast.error('❌ #1 Une erreur est survenue lors de la récupération des bons');
    }
  };

  // 🔹 Ajouter une ligne matériel
  const addMaterial = e => {
    e.preventDefault();
    setListeMaterialCreate(prev => [...prev, { ...refMaterial }]);
  };

  // 🔹 Modifier une ligne matériel (générique)
  const changeValueMaterial = (index, key, value) => {
    setListeMaterialCreate(prev => {
      const temp = [...prev];
      const line = { ...temp[index] };

      if (key === 'name') {
        const selectedMaterial = referencesMaterials.find(el => el.id === value);
        line.idMaterial = value;
        line.name = selectedMaterial?.name || '';
        line.reference = '';
      } else if (key === 'quantityLabel') {
        const numeric = extractNumericQuantity(value);
        line.quantityLabel = value;
        line.quantity = numeric === '' ? '' : numeric;
      } else {
        line[key] = value;
      }

      temp[index] = line;
      return temp;
    });
  };

  // handlers factorisés pour les inputs
  const handleMaterialSelectChange = (index, field) => e => {
    changeValueMaterial(index, field, e.target.value);
  };

  const handleQuantityChange = index => e => {
    changeValueMaterial(index, 'quantityLabel', e.target.value);
  };

  // 🔹 Supprimer une ligne matériel
  const deleteMaterial = (e, index) => {
    e.preventDefault();
    setListeMaterialCreate(prev => prev.filter((_, i) => i !== index));
  };

  // 🔹 Client + chantier
  const callGetCompany = async () => {
    try {
      const res = await getCompany(idCustomer);
      setCustomer(res);
    } catch (error) {
      console.log('Error: ', error);
      toast.error('❌ #2 Une erreur est survenue lors de la récupération des informations du client');
      window.location.href = '/clients';
    }
  };

  // 🔹 Liste materialSettings
  const callListMaterialSettings = async () => {
    try {
      const res = await getListMaterialSettings();
      const data = res.filter(item => item.name !== 'Climatiseur');
      setReferencesMaterials(data);
      setMaterials(data);
    } catch (error) {
      console.log('Error: ', error);
      toast.error('❌ #3 Une erreur est survenue lors de la récupération des matériaux');
      window.location.href = '/clients';
    }
  };

  // 🔹 Récup id Saca
  const callListSaca = async () => {
    try {
      const res = await getSacaCompany();
      setIdSaca(res[0]?.id || '');
    } catch (error) {
      console.log('Error: ', error);
      toast.error('❌ #4 Une erreur est survenue lors de la récupération des informations du client');
      window.location.href = '/clients';
    }
  };

  // 🔹 Validation du form
  const isOkForm = () => {
    if (!createName || !supplierName || !numberCoupon || !listeMaterialCreate.length) {
      return false;
    }

    for (const element of listeMaterialCreate) {
      if (!element.idMaterial) return false;
      if (!element.reference) return false;
      if (!element.quantityLabel?.trim()) return false;

      const numeric = extractNumericQuantity(element.quantityLabel);
      if (numeric === '' || Number(numeric) <= 0) return false;
    }

    return true;
  };

  // 🔹 submit
  const onSubmit = async e => {
    e.preventDefault();

    const newdata = {
      name: createName,
      supplierName,
      numero: parseInt(numberCoupon, 10),
      worksiteId: idWorksite,
      listeMaterialCreate, // chaque ligne contient quantity + quantityLabel
      idSaca,
    };

    if (isOkForm()) {
      try {
        await createOrderCoupon(newdata);
        navigate(-1);
      } catch (error) {
        console.log('error: ', error);
        toast.error('❌ #5 Une erreur est survenue lors de la création du bon');
      }
    } else {
      setFormError(true);
    }
  };

  const navigateOrder = () => navigate(-1);

  return (
    <section>
      <div className="mt-14">
        <form onSubmit={onSubmit}>
          {/* En-tête form */}
          <div className="mx-14 flex space-x-6">
            <div className="flex flex-col space-y-4">
              <label>Nom du client</label>
              <input
                className="inputFocus w-[343px] inputPreFill text-blue-admin"
                type="text"
                placeholder="Nom du client"
                value={customer?.name || ''}
                disabled
              />
            </div>
            <div className="flex flex-col space-y-4 ">
              <label>Nom du fournisseur</label>
              <input
                className="inputFocus inputPreFill w-[343px] text-blue-admin"
                type="text"
                placeholder="Sacarabany SARL"
                value={supplierName}
                disabled
              />
            </div>
            <div className="flex flex-col space-y-4 w-full">
              <label>Numéro du bon</label>
              <input
                className="inputFocus inputPreFill text-blue-admin"
                type="number"
                value={numberCoupon}
                disabled
              />
            </div>
          </div>

          <div className="flex mx-14 space-x-6">
            <div className="flex flex-col space-y-4 mt-5">
              <label>Créé par</label>
              <input
                className="inputFocus inputPreFill w-[343px] text-blue-admin"
                type="text"
                placeholder="Vétilla Zobda"
                value={createName}
                disabled
              />
            </div>
          </div>

          {/* Liste matériel */}
          <div className="mt-8 mx-14">
            <h4 className="font-semibold">Liste du matériel utilisé</h4>

            {listeMaterialCreate.map((materiel, index) => {
              const selectedMaterial = referencesMaterials.find(
                el => el.id === materiel.idMaterial
              );
              const referenceList =
                selectedMaterial?.references?.[0]?.reference
                  ?.split(',')
                  .map(ref => ref.trim())
                  .filter(Boolean) || [];

              return (
                <div key={index} className="flex space-x-5 mt-5 items-center">
                  {/* Nom matériel */}
                  <div className="flex flex-col space-y-3">
                    <label className="text-gray-placeholder">Nom du matériel</label>
                    <select
                      className="w-[234px] rounded-lg py-4 border-gray-separate text-blue-admin"
                      value={materiel.idMaterial || ''}
                      onChange={handleMaterialSelectChange(index, 'name')}
                    >
                      <option value="" disabled>
                        -
                      </option>
                      {materials.map(material => (
                        <option key={material.id} value={material.id}>
                          {material.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Référence */}
                  <div className="flex flex-col space-y-3">
                    <label className="text-gray-placeholder">Référence</label>
                    <select
                      className="w-[234px] rounded-lg py-4 border-gray-separate text-blue-admin"
                      value={materiel.reference || ''}
                      onChange={handleMaterialSelectChange(index, 'reference')}
                      disabled={!referenceList.length}
                    >
                      <option value="" disabled>
                        -
                      </option>
                      {referenceList.map((ref, indexRef) => (
                        <option key={ref} value={indexRef}>
                          {ref}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Quantité (champ libre) */}
                  <div className="flex flex-col space-y-3">
                    <label className="text-gray-placeholder">Quantité</label>
                    <input
                      className="max-w-[234px] rounded-lg py-4 border border-gray-separate placeholder:text-blue-admin text-blue-admin"
                      type="text"
                      placeholder="Ex : 3, 10 u, 250 ml"
                      value={materiel.quantityLabel || ''}
                      onChange={handleQuantityChange(index)}
                    />
                  </div>

                  {/* Delete */}
                  <div>
                    <button
                      onClick={e => deleteMaterial(e, index)}
                      className="w-8 p-2 border rounded-lg mt-7 border-white-admin"
                      type="button"
                    >
                      <img src={DeleteIcon} alt="delete icon" />
                    </button>
                  </div>
                </div>
              );
            })}

            <button
              onClick={addMaterial}
              className="text-blue-admin font-semibold mt-6 border bg-yellow-admin py-3 px-4 text-sm rounded-lg"
            >
              + Ajouter un nouveau matériel
            </button>

            {formError && (
              <p className="text-red-error text-end mt-5">
                Veuillez remplir tous les champs !
              </p>
            )}
          </div>

          {/* Footer boutons */}
          <div className="mt-16 py-4 flex justify-between items-center border-t border-white-admin">
            <button
              onClick={navigateOrder}
              className="border rounded-lg cursor-pointer ml-14 border-gray-admin px-5 py-3 text-blue-admin"
              type="button"
            >
              Annuler
            </button>

            <div className="cursor-pointer mr-14 bg-yellow-admin rounded-lg">
              <button type="submit" className="py-3 px-5 text-blue-admin">
                Enregistrer ce bon pré-rempli
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};

export default PrefillPurchaseOrder;
